import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface Workspace {
  id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  max_seats: number;
  used_seats: number;
  subscription_status: string;
  subscription_tier: string;
  enterprise_features_enabled?: boolean;
  campaigns_managed_by_admin?: boolean;
  billing_email?: string | null;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  is_international?: boolean;
  settings?: any;
  partner_id?: string | null;
}

interface WorkspaceMember {
  role: AppRole;
  workspace: Workspace;
}

export const IMPERSONATED_WORKSPACE_KEY = 'super_admin_impersonated_workspace';
export const PARTNER_IMPERSONATED_WORKSPACE_KEY = 'partner_admin_impersonated_workspace';
export const SUB_ADMIN_IMPERSONATED_WORKSPACE_KEY = 'sub_admin_impersonated_workspace';

export function useUserRole() {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [actualRole, setActualRole] = useState<AppRole | null>(null);
  const [actualPartnerId, setActualPartnerId] = useState<string | null>(null);
  const [fetchedForUserId, setFetchedForUserId] = useState<string | null>(null);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [subAdminWorkspaceIds, setSubAdminWorkspaceIds] = useState<string[]>([]);

  const fetchRoleAndWorkspace = useCallback(async () => {
    if (!user) {
      setRole(null);
      setWorkspace(null);
      setActualRole(null);
      setIsImpersonating(false);
      setLoading(false);
      return;
    }

    // We have a user but no role yet — keep loading=true so route guards
    // (e.g. AdminRoute) do not see an "authenticated but unauthorized" state
    // and incorrectly redirect to "/" before the role lookup resolves.
    setLoading(true);

    // Fail-safe: never let `loading` stay true forever. If the role/workspace
    // queries hang (e.g. transient network stall), force-clear loading after
    // 8s so route guards can resolve to a deterministic state instead of
    // spinning indefinitely on /admin/* refresh.
    const failSafe = setTimeout(() => {
      setLoading(false);
    }, 8000);

    try {
      // Check user_roles table for super_admin OR partner_admin
      const { data: roleRows } = await supabase
        .from("user_roles")
        .select("role, partner_id")
        .eq("user_id", user.id);
      // Pick the highest role if a user has several rows
      const roleData =
        (roleRows || []).find((r: any) => r.role === "super_admin") ||
        (roleRows || []).find((r: any) => r.role === "partner_admin") ||
        (roleRows || [])[0] ||
        null;

      const isSuperAdmin = roleData?.role === "super_admin";
      const isPartnerAdmin = (roleData?.role as any) === "partner_admin";
      setActualRole(isSuperAdmin ? "super_admin" : (isPartnerAdmin ? ("partner_admin" as any) : null));
      setActualPartnerId(isPartnerAdmin ? (roleData?.partner_id ?? null) : null);

      // Sub admin (delegated staff): no user_roles row, marker in sub_admins
      let subAdmin = false;
      let assignedIds: string[] = [];
      if (!isSuperAdmin && !isPartnerAdmin) {
        const { data: sa } = await (supabase as any)
          .from("sub_admins").select("is_active").eq("user_id", user.id).maybeSingle();
        if (sa?.is_active) {
          subAdmin = true;
          const { data: asg } = await (supabase as any)
            .from("sub_admin_workspaces").select("workspace_id").eq("user_id", user.id);
          assignedIds = (asg || []).map((r: any) => r.workspace_id);
        }
      }
      setIsSubAdmin(subAdmin);
      setSubAdminWorkspaceIds(assignedIds);

      const subImpersonatedId = localStorage.getItem(SUB_ADMIN_IMPERSONATED_WORKSPACE_KEY);
      if (subAdmin && subImpersonatedId) {
        if (assignedIds.includes(subImpersonatedId)) {
          const { data: sWs } = await supabase.from("workspaces").select("*").eq("id", subImpersonatedId).maybeSingle();
          if (sWs) {
            setRole("workspace_owner");
            setWorkspace(sWs as Workspace);
            setIsImpersonating(true);
            clearTimeout(failSafe);
            setLoading(false);
            return;
          }
        }
        localStorage.removeItem(SUB_ADMIN_IMPERSONATED_WORKSPACE_KEY);
      }
      if (subAdmin) {
        setRole(null);
        setWorkspace(null);
        setIsImpersonating(false);
        clearTimeout(failSafe);
        setLoading(false);
        return;
      }

      // Check if super admin is impersonating a workspace
      const impersonatedWorkspaceId = localStorage.getItem(IMPERSONATED_WORKSPACE_KEY);
      const partnerImpersonatedWorkspaceId = localStorage.getItem(PARTNER_IMPERSONATED_WORKSPACE_KEY);

      if (isSuperAdmin && impersonatedWorkspaceId) {
        // Fetch the impersonated workspace
        const { data: impersonatedWorkspace, error: wsError } = await supabase
          .from("workspaces")
          .select("*")
          .eq("id", impersonatedWorkspaceId)
          .single();

        if (!wsError && impersonatedWorkspace) {
          setRole("workspace_owner");
          setWorkspace(impersonatedWorkspace as Workspace);
          setIsImpersonating(true);
          clearTimeout(failSafe);
          setLoading(false);
          return;
        } else {
          localStorage.removeItem(IMPERSONATED_WORKSPACE_KEY);
        }
      }

      if (isPartnerAdmin && partnerImpersonatedWorkspaceId && roleData?.partner_id) {
        // Partner admin can only impersonate workspaces under their partner
        const { data: pWorkspace, error: pErr } = await supabase
          .from("workspaces")
          .select("*")
          .eq("id", partnerImpersonatedWorkspaceId)
          .eq("partner_id", roleData.partner_id)
          .maybeSingle();

        if (!pErr && pWorkspace) {
          setRole("workspace_owner");
          setWorkspace(pWorkspace as Workspace);
          setIsImpersonating(true);
          clearTimeout(failSafe);
          setLoading(false);
          return;
        } else {
          localStorage.removeItem(PARTNER_IMPERSONATED_WORKSPACE_KEY);
        }
      }

      // Normal flow - check workspace_members
      const { data: memberData, error } = await supabase
        .from("workspace_members")
        .select(`
          role,
          workspace:workspaces(*)
        `)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching workspace member data:", error);
      }

      // Set role: super_admin > partner_admin > member
      if (isSuperAdmin) {
        setRole("super_admin");
      } else if (isPartnerAdmin) {
        setRole("partner_admin" as any);
      } else if (memberData) {
        setRole(memberData.role);
      } else {
        setRole(null);
      }

      // Set workspace if user is a member of one
      if (memberData) {
        // @ts-ignore - workspace is properly joined
        setWorkspace(memberData.workspace as Workspace);
      } else {
        setWorkspace(null);
      }

      setIsImpersonating(false);
    } catch (error) {
      console.error("Error in fetchRoleAndWorkspace:", error);
      setRole(null);
      setWorkspace(null);
      setIsImpersonating(false);
    } finally {
      clearTimeout(failSafe);
      setLoading(false);
      if (user?.id) setFetchedForUserId(user.id);
    }
  }, [user?.id]); // Only recreate if user ID changes

  // Super admin function to access a specific workspace
  const impersonateWorkspace = useCallback(async (workspaceId: string) => {
    // We can check local state instead of actualRole state to avoid dependency cycles if needed,
    // but actualRole is state so it's fine.
    // However, to be safe inside useCallback, we should probably check the value from the ref or trust the caller.
    // But since we are inside the hook, we can just use the function.

    // Note: We need to verify permission, but for the UI action, checking locally is fine.
    // The fetchRoleAndWorkspace will re-verify "super_admin" from DB anyway.

    localStorage.setItem(IMPERSONATED_WORKSPACE_KEY, workspaceId);
    await fetchRoleAndWorkspace();
    return true;
  }, [fetchRoleAndWorkspace]);

  // Stop impersonating and return to normal super admin view
  const stopImpersonating = useCallback(async () => {
    localStorage.removeItem(IMPERSONATED_WORKSPACE_KEY);
    localStorage.removeItem(PARTNER_IMPERSONATED_WORKSPACE_KEY);
    localStorage.removeItem(SUB_ADMIN_IMPERSONATED_WORKSPACE_KEY);
    await fetchRoleAndWorkspace();
  }, [fetchRoleAndWorkspace]);

  // Sub admin: enter an assigned workspace (re-verified against DB in fetch)
  const impersonateWorkspaceAsSubAdmin = useCallback(async (workspaceId: string) => {
    localStorage.setItem(SUB_ADMIN_IMPERSONATED_WORKSPACE_KEY, workspaceId);
    await fetchRoleAndWorkspace();
    return true;
  }, [fetchRoleAndWorkspace]);

  // Partner admin function to access a workspace they own
  const impersonateWorkspaceAsPartner = useCallback(async (workspaceId: string) => {
    localStorage.setItem(PARTNER_IMPERSONATED_WORKSPACE_KEY, workspaceId);
    await fetchRoleAndWorkspace();
    return true;
  }, [fetchRoleAndWorkspace]);

  useEffect(() => {
    // While auth itself is still resolving on hard refresh, do NOT clear the
    // role state to "no role" — that would cause guards like AdminRoute to
    // briefly see an authenticated user with no role and redirect away.
    if (authLoading) {
      setLoading(true);
      return;
    }

    // If user is null (truly signed out), reset everything immediately.
    if (!user) {
      setFetchedForUserId(null);
      setRole(null);
      setWorkspace(null);
      setActualRole(null);
      setActualPartnerId(null);
      setIsSubAdmin(false);
      setSubAdminWorkspaceIds([]);
      setIsImpersonating(false);
      setLoading(false);
      return;
    }

    // Skip refetch when the user id is unchanged (e.g. silent token refresh).
    if (fetchedForUserId === user.id) {
      return;
    }
    // New user id — synchronously mark loading so route guards do not briefly
    // see an authenticated user with no role and redirect away.
    setLoading(true);

    fetchRoleAndWorkspace();
  }, [fetchRoleAndWorkspace, user, authLoading, fetchedForUserId]);

  // Effective loading: also true when auth has a user but we haven't yet
  // completed a fetch for that user id. Prevents a one-render gap (after
  // sign-out → sign-in as different user) where stale {role:null, loading:false}
  // would cause RoleBasedLayout to redirect to /onboarding.
  const effectiveLoading =
    authLoading || loading || (!!user && fetchedForUserId !== user.id);

  return {
    role,
    workspace,
    loading: effectiveLoading,
    refetch: fetchRoleAndWorkspace,
    // These reflect the effective role (workspace_owner when impersonating)
    isSuperAdmin: role === "super_admin",
    isWorkspaceOwner: role === "workspace_owner",
    isWorkspaceAdmin: role === "workspace_admin",
    isMember: role === "member",
    isWorkspaceAdminOrOwner: role === "workspace_owner" || role === "workspace_admin",
    // Super admin impersonation
    isImpersonating,
    actualRole,
    actualPartnerId,
    isActuallySuperAdmin: actualRole === "super_admin",
    isActuallyPartnerAdmin: (actualRole as any) === "partner_admin",
    impersonateWorkspace,
    impersonateWorkspaceAsPartner,
    stopImpersonating,
    isActuallySubAdmin: isSubAdmin,
    subAdminWorkspaceIds,
    impersonateWorkspaceAsSubAdmin,
  };
}
